import React from "react";
import { Document, Page, Text, Image, StyleSheet, View } from "@react-pdf/renderer";

export interface RecordPDFDocumentProps {
  data: any;
  title: string;
}

const pdfStyles = StyleSheet.create({
  page: {
    paddingTop: 48,
    paddingHorizontal: 40,
    paddingBottom: 32,
    fontSize: 11,
    fontFamily: "Helvetica",
    lineHeight: 1.45,
    color: "#111",
    backgroundColor: "#ffffff",
  },
  title: {
    fontSize: 24,
    marginBottom: 24,
    fontWeight: "bold",
    textAlign: "center" as const,
    color: "#000",
  },
  divider: {
    height: 1,
    backgroundColor: "#e5e5e5",
    marginVertical: 24,
  },
  sectionTitle: {
    fontSize: 12,
    marginTop: 24,
    marginBottom: 8,
    fontWeight: "bold",
    color: "#03a9f4",
    textTransform: "uppercase" as const,
    letterSpacing: 1,
  },
  row: {
    flexDirection: "row" as const,
    marginBottom: 6,
  },
  key: {
    color: "#555",
    fontSize: 10,
    textTransform: "uppercase" as const,
    letterSpacing: 0.5,
    marginBottom: 2,
  },
  value: {
    color: "#000",
    fontSize: 11,
  },
  valueCode: {
    color: "#000",
    fontFamily: "Courier",
    fontSize: 11,
    backgroundColor: "#f5f5f5",
    padding: 4,
    borderRadius: 4,
    wordBreak: "break-all" as const,
  },
  img: {
    width: 360,
    height: "auto",
    marginBottom: 24,
    alignSelf: "center",
    borderRadius: 4,
  },
});

const prettifyKey = (key: string) => key.replace(/([A-Z])/g, " $1").replace(/^./, (str) => str.toUpperCase());

const renderEntries = (obj: Record<string, any>, level: number = 0): any =>
  Object.entries(obj).flatMap(([k, v]) => {
    // skip undefined/null and internal helper fields
    if (v === null || v === undefined || k === "imageBase64" || k === "blockchainData") return [];

    // If value is an object, recurse WITHOUT rendering an extra label row – this flattens
    // nested group keys like "deviceInfo" or "blockchainData" so only the inner fields appear.
    if (typeof v === "object" && !Array.isArray(v)) {
      return renderEntries(v, level + 1);
    }

    // Primitive value → render key row above value row (single-column stack)
    return (
      <View key={k + level} style={{ marginBottom: 15 }}>
        <Text style={pdfStyles.key}>{prettifyKey(k)}</Text>
        <Text style={String(v).length > 40 ? pdfStyles.valueCode : pdfStyles.value}>{String(v)}</Text>
      </View>
    );
  });

export const RecordPDFDocument: React.FC<RecordPDFDocumentProps> = ({ data, title }) => {
  const blockchain = (data as any).blockchainData ?? (data.deviceData as any)?.blockchainData;

  return (
    <Document>
      <Page size="A4" style={pdfStyles.page} wrap>
        <Text style={pdfStyles.title}>{title}</Text>
        <View style={pdfStyles.divider} />

        {data.deviceData?.imageBase64 && <Image src={data.deviceData.imageBase64} style={pdfStyles.img} />}

        <Text style={pdfStyles.sectionTitle}>Device Information</Text>
        {renderEntries({
          ...data.deviceData,
          imageBase64: undefined,
          blockchainData: undefined,
        })}

        {blockchain && (
          <>
            <Text style={pdfStyles.sectionTitle}>Blockchain Data</Text>
            {renderEntries(blockchain)}
          </>
        )}
      </Page>
    </Document>
  );
};
